import React, { useState } from "react";
import { MoreVertical, X, LogIn, Clock, AlertCircle, CreditCard } from "lucide-react";
import { Constants } from "../../../utils/constants";
import { canModify, canCheckIn, canCheckOut, canCancel } from "../../../utils/bookingActionsUtils";
import BookingActionsModal from "./BookingActionsModal";
import logger from "../../../utils/logger";


const BookingActionsCard = ({
  booking,
  style,
  formatTimeRange,
  onModify,
  onCancel,
  onCheckIn,
  onCheckOut,
  showConfirmation,
}) => {
  const [showActionsModal, setShowActionsModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Get booking status for display
  const getBookingStatus = () => {
    if (!booking.status) return "pending";
    return booking.status.toLowerCase();
  };

  const getStatusColor = () => {
    const status = getBookingStatus();
    switch (status) {
      case "checked_in":
        return "bg-blue-100 text-blue-800";
      case "pending_payment":
        return "bg-amber-100 text-amber-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
      case "canceled":
      case "no_show":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = () => {
    const status = getBookingStatus();
    switch (status) {
      case "checked_in":
        return <LogIn className="w-3 h-3" />;
      case "pending_payment":
        return <CreditCard className="w-3 h-3" />;
      case "pending":
        return <Clock className="w-3 h-3" />;
      case "cancelled":
      case "canceled":
        return <X className="w-3 h-3" />;
      default:
        return <AlertCircle className="w-3 h-3" />;
    }
  };

  const handleAction = async (action, actionName) => {
    if (isProcessing) return;

    setIsProcessing(true);
    setShowActionsModal(false);

    try {
      await action();
    } catch (error) {
      logger.error(`Error performing ${actionName}:`, error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="relative">
      <div
        className="overflow-hidden p-2 mr-2 ml-16 text-white rounded-2xl md:rounded-3xl md:p-2 md:ml-20 cursor-pointer"
        style={{
          ...style,
          backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
          maxWidth: "calc(100% - 72px)",
          height: "auto",
          minHeight: style.height,
        }}
        onClick={() => {
          // Allow opening the modal even for terminal statuses to view details,
          // but actions inside will be restricted by their respective canX helpers
          setShowActionsModal(true);
        }}
      >
        <div className="flex items-start space-x-1 h-full md:space-x-2">
          <div className="overflow-hidden flex-shrink-0 w-6 h-6 bg-white bg-opacity-20 rounded-full md:w-8 md:h-8">
            <img
              src={booking.avatar}
              alt={booking.name}
              className="object-cover w-full h-full"
              onError={e => {
                e.target.style.display = "none";
                e.target.parentNode.innerHTML =
                  '<div class="flex justify-center items-center w-full h-full text-xs font-medium text-white bg-white bg-opacity-20">' +
                  booking.name
                    .split(" ")
                    .map(n => n[0])
                    .join("") +
                  "</div>";
              }}
            />
          </div>

          <div className="flex flex-col flex-1 justify-center min-w-0">
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="text-xs font-semibold leading-tight truncate">{booking.name}</div>
                {booking.instanceDisplayName && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-white bg-opacity-30 rounded text-white whitespace-nowrap flex-shrink-0">
                    {booking.instanceDisplayName}
                  </span>
                )}
              </div>
              <div
                className={`px-2 py-0.5 text-xs rounded-full flex items-center space-x-1 flex-shrink-0 ${getStatusColor()}`}
              >
                {getStatusIcon()}
                <span className="capitalize">{getBookingStatus().replace("_", " ")}</span>
              </div>
            </div>
            <div className="hidden text-xs leading-tight truncate opacity-90 md:block">{booking.email}</div>
            <div className="text-xs opacity-90 mt-0.5 font-medium">
              {formatTimeRange(booking.startTime, booking.endTime)}
            </div>
          </div>

          {/* Always show the icon so user knows it's clickable for details */}
          <div className="relative flex-shrink-0">
            <div className="p-1 text-white bg-white bg-opacity-20 rounded-full">
              <MoreVertical className="w-4 h-4" />
            </div>
          </div>
        </div>
      </div>

      {/* Booking Actions Modal */}
      <BookingActionsModal
        isOpen={showActionsModal}
        onClose={() => setShowActionsModal(false)}
        booking={booking}
        onModify={onModify}
        onCancel={onCancel}
        onCheckIn={onCheckIn}
        onCheckOut={onCheckOut}
        formatTimeRange={formatTimeRange}
        showConfirmation={showConfirmation}
      />
    </div>
  );
};

export default BookingActionsCard;
